import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import { supabase } from '../config/supabase'
import { format } from 'date-fns'

const {
  FiArrowLeft,
  FiDownload,
  FiCalendar,
  FiFileText,
  FiAlertCircle
} = FiIcons

const CertificateViewer = () => {
  const { lotNumber } = useParams()

  const [certificates, setCertificates] = useState([])
  const [selectedCertificate, setSelectedCertificate] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pdfUrl, setPdfUrl] = useState(null)

  useEffect(() => {
    fetchCertificates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotNumber])

  const fetchCertificates = async () => {
    try {
      setLoading(true)
      setError(null)

      const { data, error } = await supabase
        .from('certificates')
        .select('*')
        .eq('lot_number', lotNumber)
        .order('created_at', { ascending: false })

      if (error) throw error

      setCertificates(data || [])
      setSelectedCertificate(data?.[0] || null)
      setPdfUrl(null)
    } catch (err) {
      console.error(err)
      setError('Failed to load certificates')
    } finally {
      setLoading(false)
    }
  }

  const openPdf = async (certificate) => {
    const cleanPath = certificate.file_path?.trim()

    if (!cleanPath) {
      alert('Invalid file path')
      return
    }

    const { data, error } = await supabase.storage
      .from('coa-certificates')
      .createSignedUrl(cleanPath, 300)

    if (error) {
      console.error(error)
      alert('Unable to load PDF')
      return
    }

    setPdfUrl(`${data.signedUrl}#toolbar=0&navpanes=0&scrollbar=0`)
  }

  /* ---------------- STATES ---------------- */

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 text-center">
        <div className="animate-spin h-8 w-8 border-b-2 border-primary-600 mx-auto mb-3" />
        Loading certificates…
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <SafeIcon icon={FiAlertCircle} className="text-red-500 text-4xl mb-4 mx-auto" />
        <p>{error}</p>
        <Link to="/" className="text-primary-600 block mt-4">
          ← Back
        </Link>
      </div>
    )
  }

  if (certificates.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <SafeIcon icon={FiFileText} className="text-gray-400 text-4xl mb-4 mx-auto" />
        <p>No certificates found for lot <strong>{lotNumber}</strong></p>
      </div>
    )
  }

  /* ---------------- UI ---------------- */

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <Link to="/" className="text-primary-600 inline-flex items-center mb-4">
        <SafeIcon icon={FiArrowLeft} className="mr-2" />
        Back to Search
      </Link>

      <div className="bg-white border rounded-lg p-6 mb-6">
        <h1 className="text-2xl font-semibold">
          Certificates for Lot: {lotNumber}
        </h1>
        <p className="text-gray-600">
          {certificates.length} certificate(s)
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* LEFT PANEL */}
        <div className="bg-white border rounded-lg">
          <div className="p-4 border-b font-medium">
            Available Certificates
          </div>
          <div className="p-2">
            {certificates.map(cert => (
              <button
                key={cert.id}
                onClick={() => {
                  setSelectedCertificate(cert)
                  openPdf(cert)
                }}
                className={`w-full p-3 text-left rounded mb-1 ${
                  selectedCertificate?.id === cert.id
                    ? 'bg-primary-50 border border-primary-200'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="font-medium text-sm">
                  {cert.certificate_name}
                </div>

                {cert.test_date && (
                  <div className="text-xs text-gray-500 mt-1 flex items-center">
                    <SafeIcon icon={FiCalendar} className="mr-1" />
                    {format(new Date(cert.test_date), 'MMM d, yyyy')}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="lg:col-span-3 bg-white border rounded-lg">
          <div className="p-6 border-b flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">
                {selectedCertificate.certificate_name}
              </h3>
              {selectedCertificate.test_date && (
                <p className="text-sm text-gray-600">
                  Test Date:{' '}
                  {format(
                    new Date(selectedCertificate.test_date),
                    'MMMM d, yyyy'
                  )}
                </p>
              )}
            </div>

            <span className="text-sm text-gray-400 flex items-center">
              <SafeIcon icon={FiDownload} className="mr-1" />
              Download disabled
            </span>
          </div>

          {/* INLINE PDF */}
          <div className="p-4 h-[700px]">
            {pdfUrl ? (
              <iframe
                src={pdfUrl}
                title="Certificate PDF"
                className="w-full h-full border rounded"
              />
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-gray-500">
                <SafeIcon icon={FiFileText} className="text-4xl mb-3" />
                <p>Select a certificate to view</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CertificateViewer
